import { useState, useEffect } from 'react';
import { eventsApi } from '../../lib/api';

const cardStyle = { background: 'rgba(255,255,255,0.97)', border: '1px solid #E2E8F0', borderRadius: 20, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', padding: 24 };

const STATUS_STYLES = {
  received: { bg: '#F1F5F9', color: '#64748B' },
  under_review: { bg: '#EEF2FF', color: '#4F46E5' },
  approved: { bg: '#ECFDF5', color: '#10B981' },
  rejected: { bg: '#FEF2F2', color: '#F97066' },
  withdrawn: { bg: '#F1F5F9', color: '#94A3B8' },
};

const PAYMENT_STYLES = {
  pending: { bg: '#FFFBEB', color: '#F59E0B' },
  paid: { bg: '#ECFDF5', color: '#10B981' },
  revenue_share: { bg: '#EEF2FF', color: '#4F46E5' },
  refunded: { bg: '#FEF2F2', color: '#F97066' },
};

export default function SubmissionsList({ eventId, onRefresh }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSubmissions(); }, [eventId]);

  const loadSubmissions = async () => {
    try { const data = await eventsApi.getSubmissions(eventId); setSubmissions(data.submissions || []); }
    catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleStatusChange = async (subId, newStatus) => {
    try {
      await eventsApi.updateSubmission(eventId, subId, { submission_status: newStatus });
      await loadSubmissions();
      if (selected?.id === subId) {
        setSelected(prev => ({ ...prev, submission_status: newStatus }));
      }
    } catch (err) { console.error(err); }
  };

  const handlePaymentChange = async (subId, newStatus) => {
    try {
      await eventsApi.updateSubmission(eventId, subId, { payment_status: newStatus });
      await loadSubmissions();
    } catch (err) { console.error(err); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 48, color: '#94A3B8' }}>Loading...</div>;

  // Detail view
  if (selected) {
    const s = selected;
    const ss = STATUS_STYLES[s.submission_status] || STATUS_STYLES.received;
    const ps = PAYMENT_STYLES[s.payment_status] || PAYMENT_STYLES.pending;

    return (
      <div>
        <button onClick={() => setSelected(null)} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#94A3B8', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 16, fontFamily: 'Poppins, sans-serif' }}>
          ← Back to Submissions
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <h2 style={{ fontFamily: '"EB Garamond", serif', fontSize: 22, color: '#0F172A', margin: '0 0 4px' }}>{s.product_name}</h2>
                <p style={{ fontSize: 13, color: '#94A3B8', fontFamily: 'Poppins, sans-serif', margin: 0 }}>by {s.inventor_name} {s.studio_name && `(${s.studio_name})`}</p>
              </div>
              <span style={{ padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: ss.bg, color: ss.color, fontFamily: 'Poppins, sans-serif', height: 'fit-content', textTransform: 'capitalize' }}>
                {s.submission_status?.replace(/_/g, ' ')}
              </span>
            </div>

            <div style={{ fontSize: 13, fontFamily: 'Poppins, sans-serif', color: '#64748B', lineHeight: 1.8 }}>
              <p><strong>Email:</strong> {s.inventor_email}</p>
              {s.sizzle_video_url && <p><strong>Sizzle Video:</strong> <a href={s.sizzle_video_url} target="_blank" rel="noreferrer" style={{ color: '#c9a84c' }}>{s.sizzle_video_url}</a></p>}
              {s.ppt_file_url && <p><strong>PPT:</strong> <a href={s.ppt_file_url} target="_blank" rel="noreferrer" style={{ color: '#c9a84c' }}>Download</a></p>}
              {s.backup_description && <p><strong>Description:</strong> {s.backup_description}</p>}
              {s.already_pitched_to?.length > 0 && <p><strong>Already Pitched To:</strong> {s.already_pitched_to.join(', ')}</p>}
              {s.suggested_companies?.length > 0 && <p><strong>Suggested Companies:</strong> {s.suggested_companies.join(', ')}</p>}
              <p><strong>Payment:</strong> <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: ps.bg, color: ps.color }}>{s.payment_status?.replace(/_/g, ' ')}</span> ({s.payment_option?.replace(/_/g, ' ')})</p>
            </div>

            {/* Download buttons */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 16, borderTop: '1px solid #E2E8F0' }}>
              <a href={`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/events/${eventId}/submissions/${s.id}/pdf`}
                target="_blank" rel="noreferrer"
                style={{ padding: '8px 16px', borderRadius: 12, background: '#0F172A', color: 'white', fontSize: 12, fontWeight: 600, fontFamily: 'Poppins, sans-serif', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                📄 Download PDF
              </a>
              {s.ppt_file_url && (
                <a href={s.ppt_file_url} target="_blank" rel="noreferrer"
                  style={{ padding: '8px 16px', borderRadius: 12, background: '#EEF2FF', color: '#4F46E5', fontSize: 12, fontWeight: 600, fontFamily: 'Poppins, sans-serif', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                  📎 Download PPT
                </a>
              )}
              {s.sizzle_video_url && (
                <a href={s.sizzle_video_url} target="_blank" rel="noreferrer"
                  style={{ padding: '8px 16px', borderRadius: 12, background: '#ECFDF5', color: '#10B981', fontSize: 12, fontWeight: 600, fontFamily: 'Poppins, sans-serif', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                  🎬 Watch Video
                </a>
              )}
            </div>
          </div>

          <div>
            <div style={cardStyle}>
              <h3 style={{ fontFamily: '"EB Garamond", serif', fontSize: 18, color: '#0F172A', margin: '0 0 12px' }}>Review</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {['received', 'under_review', 'approved', 'rejected'].map(status => (
                  <button key={status} onClick={() => handleStatusChange(s.id, status)} style={{
                    padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600, fontFamily: 'Poppins, sans-serif',
                    border: 'none', cursor: 'pointer', textTransform: 'capitalize', textAlign: 'left',
                    background: s.submission_status === status ? STATUS_STYLES[status].bg : '#F8FAFC',
                    color: s.submission_status === status ? STATUS_STYLES[status].color : '#94A3B8',
                    boxShadow: s.submission_status === status ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                  }}>
                    {status.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>

              <h3 style={{ fontFamily: '"EB Garamond", serif', fontSize: 18, color: '#0F172A', margin: '20px 0 12px' }}>Payment</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {['pending', 'paid', 'revenue_share'].map(ps => (
                  <button key={ps} onClick={() => handlePaymentChange(s.id, ps)} style={{
                    padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600, fontFamily: 'Poppins, sans-serif',
                    border: 'none', cursor: 'pointer', textTransform: 'capitalize', textAlign: 'left',
                    background: s.payment_status === ps ? PAYMENT_STYLES[ps].bg : '#F8FAFC',
                    color: s.payment_status === ps ? PAYMENT_STYLES[ps].color : '#94A3B8',
                  }}>
                    {ps.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontFamily: '"EB Garamond", serif', fontSize: 22, color: '#0F172A', margin: 0 }}>
          Submissions ({submissions.length})
        </h2>
        {submissions.length > 0 && (
          <a href={`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/events/${eventId}/submissions/export/pdf`}
            target="_blank" rel="noreferrer"
            style={{ padding: '8px 16px', borderRadius: 12, background: '#0F172A', color: 'white', fontSize: 12, fontWeight: 600, fontFamily: 'Poppins, sans-serif', textDecoration: 'none' }}>
            📄 Export All as PDF
          </a>
        )}
      </div>

      {submissions.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 48 }}>
          <p style={{ fontSize: 14, color: '#94A3B8', fontFamily: 'Poppins, sans-serif' }}>No submissions yet. Share the form link with inventors.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {submissions.map(s => {
            const ss = STATUS_STYLES[s.submission_status] || STATUS_STYLES.received;
            const ps = PAYMENT_STYLES[s.payment_status] || PAYMENT_STYLES.pending;
            return (
              <button key={s.id} onClick={() => setSelected(s)} style={{ ...cardStyle, textAlign: 'left', cursor: 'pointer', width: '100%', padding: 16, transition: 'box-shadow 0.2s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: 14, fontWeight: 600, color: '#0F172A' }}>{s.product_name}</div>
                    <div style={{ fontSize: 12, color: '#94A3B8', fontFamily: 'Poppins, sans-serif', marginTop: 2 }}>
                      {s.inventor_name} {s.studio_name && `· ${s.studio_name}`} · {new Date(s.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: ps.bg, color: ps.color, fontFamily: 'Poppins, sans-serif' }}>
                      {s.payment_status?.replace(/_/g, ' ')}
                    </span>
                    <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: ss.bg, color: ss.color, fontFamily: 'Poppins, sans-serif', textTransform: 'capitalize' }}>
                      {s.submission_status?.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
